<section class="benutzerverwaltung-page">
    <header class="page-header">
        <div>
            <h1>Benutzerverwaltung</h1>
            <p>
                {{ isCopagoAdminMode()
                ? 'Tenants und zugehörige SuperAdmins verwalten'
                : 'Rollen, Benutzer und Berechtigungen verwalten'
                }}
            </p>
        </div>
    </header>

    <div class="tabs">
        <button type="button" class="tab-button" [class.tab-button-active]="activeTab() === 'benutzer'"
            [class.tab-button-disabled]="hasNoRoles()" [disabled]="hasNoRoles()" (click)="setActiveTab('benutzer')">
            Benutzer
        </button>

        @if (!isCopagoAdminMode()) {
        <button type="button" class="tab-button" [class.tab-button-active]="activeTab() === 'rollen'"
            (click)="setActiveTab('rollen')">
            Rollen
        </button>
        }
    </div>

    @if (hasNoRoles()) {
    <div class="role-required-banner">
        <mat-icon fontSet="material-icons-outlined">warning</mat-icon>

        <span>
            Es ist noch keine Rolle vorhanden. Bitte erstellen Sie zuerst eine Rolle.
        </span>
    </div>
    }

    @if (canManageBestellungSettings()) {
    <div class="bestellung-settings-card">
        <div class="bestellung-settings-info">
            <mat-icon fontSet="material-icons-outlined">schedule</mat-icon>

            <div>
                <strong>Bestellzeitraum</strong>
                <span>
                    Steuert, ob Bestellungen ganztägig oder nur innerhalb des Zeitfensters bearbeitet werden können.
                </span>
            </div>
        </div>

        <mat-slide-toggle class="bestellung-settings-toggle" [checked]="orderCanBeChangedContinuously()"
            [disabled]="isBestellungSettingsLoading() || isBestellungSettingsSaving()"
            (change)="updateOrderCanBeChangedContinuously($event.checked)">
            {{ orderCanBeChangedContinuously() ? 'Durchgehend' : 'Zeitfenster' }}
        </mat-slide-toggle>
    </div>
    }

    @if (activeTab() === 'benutzer') {
    <div class="toolbar">
        <div class="toolbar-left">
            <div class="user-search">
                <mat-icon fontSet="material-icons-outlined">search</mat-icon>

                <input type="text" placeholder="Benutzer suchen..." [value]="searchTerm()"
                    (input)="updateSearchTerm($event)" />

                @if (searchTerm()) {
                <button type="button" class="search-clear-button" (click)="searchTerm.set('')">
                    <mat-icon fontSet="material-icons-outlined">
                        close
                    </mat-icon>
                </button>
                }
            </div>

            @if (!isCopagoAdminMode() && selectedIds().length > 0) {
            <button type="button" class="secondary-button" (click)="rollenAendern()">
                <mat-icon fontSet="material-icons-outlined">groups</mat-icon>
                Rollen ändern ({{ selectedIds().length }})
            </button>
            }
        </div>

        <div class="toolbar-right">
            <button type="button" class="primary-button" (click)="createBenutzer()">
                <mat-icon fontSet="material-icons-outlined">person_add</mat-icon>
                Benutzer anlegen
            </button>
        </div>
    </div>

    <div class="table-card" [class.empty-result]="!isLoading() && !filteredBenutzer().length">
        @if (isLoading()) {
        <div class="table-scroll">
            <table class="users-table users-table-skeleton">
                <thead>
                    @if (isCopagoAdminMode()) {
                    <tr>
                        <th>TID</th>
                        <th>SuperAdmin E-Mail</th>
                        <th>SuperAdmin Name</th>
                        <th>Status</th>
                        <th class="action-column"></th>
                    </tr>
                    } @else {
                    <tr>
                        <th class="checkbox-column"></th>
                        <th>Benutzer</th>
                        <th>Name</th>
                        <th>Rollen</th>
                        <th>Filialen</th>
                        <th>Module</th>
                        <th class="action-column"></th>
                    </tr>
                    }
                </thead>

                <tbody>
                    @for (row of skeletonRows; track $index) {
                    @if (isCopagoAdminMode()) {
                    <tr>
                        <td>
                            <span class="skeleton skeleton-text skeleton-text-strong"></span>
                        </td>

                        <td>
                            <span class="skeleton skeleton-text skeleton-text-medium"></span>
                        </td>

                        <td>
                            <span class="skeleton skeleton-text skeleton-text-small"></span>
                        </td>

                        <td>
                            <span class="skeleton skeleton-text skeleton-text-small"></span>
                        </td>

                        <td class="action-column">
                            <span class="skeleton skeleton-icon"></span>
                        </td>
                    </tr>
                    } @else {
                    <tr>
                        <td class="checkbox-column">
                            <span class="skeleton skeleton-checkbox"></span>
                        </td>

                        <td>
                            <span class="skeleton skeleton-text skeleton-text-strong"></span>
                        </td>

                        <td>
                            <span class="skeleton skeleton-text skeleton-text-medium"></span>
                        </td>

                        <td>
                            <div class="skeleton-chip-list">
                                <span class="skeleton skeleton-chip"></span>
                                <span class="skeleton skeleton-chip skeleton-chip-small"></span>
                            </div>
                        </td>

                        <td>
                            <span class="skeleton skeleton-text skeleton-text-small"></span>
                        </td>

                        <td>
                            <div class="skeleton-chip-list">
                                <span class="skeleton skeleton-chip"></span>
                            </div>
                        </td>

                        <td class="action-column">
                            <span class="skeleton skeleton-icon"></span>
                        </td>
                    </tr>
                    }
                    }
                </tbody>
            </table>
        </div>
        } @else if (!filteredBenutzer().length) {
        <div class="empty-search-state">
            <mat-icon fontSet="material-icons-outlined">
                search_off
            </mat-icon>

            <strong>Kein Benutzer gefunden</strong>
        </div>
        } @else {
        <div class="table-scroll">
            <table class="users-table">
                @if (isCopagoAdminMode()) {
                <thead>
                    <tr>
                        <th>TID</th>
                        <th>SuperAdmin E-Mail</th>
                        <th>SuperAdmin Name</th>
                        <th>Status</th>
                        <th class="action-column"></th>
                    </tr>
                </thead>

                <tbody>
                    @for (item of filteredBenutzer(); track item.id) {
                    <tr class="user-row" (click)="onUserRowClick(item)">
                        <td data-label="TID">
                            {{ item.id }}
                        </td>

                        <td class="strong-cell" data-label="SuperAdmin E-Mail">
                            {{ item.email }}
                        </td>

                        <td data-label="SuperAdmin Name">
                            {{ item.name }}
                        </td>

                        <td data-label="Status">
                            <mat-slide-toggle class="tenant-status-toggle" [checked]="item.aktiv"
                                (click)="$event.stopPropagation()" (change)="updateTenantStatus(item, $event.checked)">
                                {{ item.aktiv ? 'Aktiv' : 'Inaktiv' }}
                            </mat-slide-toggle>
                        </td>

                        <td class="action-column">
                            <div class="action-buttons">
                                <button type="button" class="icon-action-button"
                                    (click)="$event.stopPropagation(); editBenutzer(item)"
                                    aria-label="SuperAdmin bearbeiten">
                                    <mat-icon fontSet="material-icons-outlined">edit</mat-icon>
                                </button>
                            </div>
                        </td>
                    </tr>
                    }
                </tbody>
                } @else {
                <thead>
                    <tr>
                        <th class="checkbox-column">
                            <span class="fake-checkbox" [class.checked]="isAllSelected()"
                                (click)="toggleSelectAll()"></span>
                        </th>
                        <th>Benutzer</th>
                        <th>Name</th>
                        <th>Rollen</th>
                        <th>Filialen</th>
                        <th>Module</th>
                        <th class="action-column"></th>
                    </tr>
                </thead>

                <tbody>
                    @for (item of filteredBenutzer(); track item.id) {
                    <tr class="user-row" (click)="onUserRowClick(item)">
                        <td class="checkbox-column">
                            @if (canSelectUser(item)) {
                            <span class="fake-checkbox" [class.checked]="isSelected(item.id)"
                                (click)="$event.stopPropagation(); toggleSelection(item)"></span>
                            }
                        </td>

                        <td class="strong-cell" data-label="Benutzer">
                            {{ item.email }}
                        </td>

                        <td data-label="Name">
                            {{ item.name }}
                        </td>

                        <td data-label="Rollen">
                            <div class="chip-list">
                                @for (rolle of visibleItems(item.rollen, 1); track rolle.id) {
                                <span class="chip role-chip" [class.role-chip-admin]="rolle.istAdminRolle">
                                    @if (rolle.istAdminRolle) {
                                    <mat-icon fontSet="material-icons-outlined">shield</mat-icon>
                                    }

                                    {{ rolle.name }}
                                </span>
                                }

                                @if (hiddenItemsCount(item.rollen, 1) > 0) {
                                <span class="chip chip-more">
                                    +{{ hiddenItemsCount(item.rollen, 1) }}
                                </span>
                                }
                            </div>
                        </td>

                        <td data-label="Filialen">
                            {{ getFilialenText(item) }}
                        </td>

                        <td data-label="Module">
                            <div class="module-list">
                                @if (item.module.includes('Alle')) {
                                <span class="chip chip-primary">Alle (Admin)</span>
                                } @else {
                                @for (modul of visibleItems(item.module, 1); track modul) {
                                <span class="module-chip">{{ modul }}</span>
                                } @empty {
                                <span>-</span>
                                }

                                @if (hiddenItemsCount(item.module, 1) > 0) {
                                <span class="module-chip chip-more">
                                    +{{ hiddenItemsCount(item.module, 1) }}
                                </span>
                                }
                                }
                            </div>
                        </td>

                        <td class="action-column">
                            <div class="action-buttons">
                                <button type="button" class="icon-action-button"
                                    (click)="$event.stopPropagation(); editBenutzer(item)"
                                    aria-label="Benutzer bearbeiten">
                                    <mat-icon fontSet="material-icons-outlined">edit</mat-icon>
                                </button>

                                @if (!isCurrentUser(item)) {
                                <button type="button" class="icon-action-button delete-action-button"
                                    (click)="$event.stopPropagation(); deleteBenutzer(item)"
                                    aria-label="Benutzer löschen">
                                    <mat-icon fontSet="material-icons-outlined">delete</mat-icon>
                                </button>
                                }
                            </div>
                        </td>
                    </tr>
                    }
                </tbody>
                }
            </table>
        </div>
        }
    </div>
    }

    @if (!isCopagoAdminMode() && activeTab() === 'rollen') {
    <app-rollen-tab (rollenChanged)="checkSystemRoles()" />
    }
</section>